import type { FunctionComponent, MouseEvent, PointerEvent } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconThreeDots } from '~/ui/core/icons';

type SkillContextMenuProps = {
    readonly onDelete: () => void;
    readonly onShowSkillDetails: () => void;
    readonly onTriggerProficiencyDropdown: () => void;
};

export const SkillContextMenu: FunctionComponent<SkillContextMenuProps> = ({
    onDelete,
    onShowSkillDetails,
    onTriggerProficiencyDropdown,
}) => {
    const { translate } = useContext(ApplicationContext);
    const [isOpen, setIsOpen] = useState(false);

    const handleShowSkillDetails = (): void => {
        setIsOpen(false);
        setTimeout(() => {
            onShowSkillDetails();
        }, 100);
    };

    const handleChangeProficiencyLevel = (): void => {
        setIsOpen(false);
        requestAnimationFrame(() => {
            onTriggerProficiencyDropdown();
        });
    };

    const handleDelete = (): void => {
        setIsOpen(false);
        requestAnimationFrame(() => {
            onDelete();
        });
    };

    const stopRowActivation = (event: MouseEvent<HTMLElement> | PointerEvent<HTMLElement>): void => {
        event.stopPropagation();
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger>
                <Button
                    data-testid="three-dot-menu"
                    iconOnly={true}
                    size="sm"
                    variant="tertiary"
                    onClick={stopRowActivation}
                    onPointerDown={stopRowActivation}
                >
                    <IconThreeDots size={16} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent onClick={stopRowActivation} onPointerDown={stopRowActivation}>
                <DropdownMenuItem onClick={handleShowSkillDetails}>
                    {translate('pages.job-architecture-v2.show-skill-details')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleChangeProficiencyLevel}>
                    {translate('pages.job-architecture-v2.change-proficiency-level')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete}>{translate('common.delete')}</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
